import { FormControl, ValidationErrors } from '@angular/forms';
import { FormPeselValidator } from './form-pesel-validator';
import { EmailValidator } from 'commons-validator-js';
import * as moment from 'moment';

type ExtendedValidationErrors = ValidationErrors & {
  [key: string]: null | { error: boolean, msg?: string };
};


export class FormCustomValidators {

  static onlyLetters(field: FormControl): ExtendedValidationErrors | null {
    const isValid = /^[a-zżźćńółęąś\s\-]+$/i.test(field.value);

    const rule = {
      onlyLetters: {
        error: true,
        msg: 'Pole może zawierać tylko litery'
      }
    };

    return isValid ? null : rule;
  }

  static smsCode(field: FormControl): ExtendedValidationErrors | null {
    const correctValue = /^\d{4,}$/.test(field?.value);

    if (!correctValue) {
      return {
        smsCode:
        {
          error: true,
          msg: 'Podane hasło jest nieprawidłowe'
        }
      };
    } else if (!field?.value?.length) {
      return {
        smsCode:
        {
          error: true,
          msg: 'Pole jest wymagane'
        }
      };
    }
    else {
      return null;
    }
  }

  static firstName(field: FormControl): ExtendedValidationErrors {
    const isValid = /^[a-zżźćńółęąś\s\-]+$/i.test(field.value);

    if (!field.value.length) {
      return {
        firstName: {
          error: true,
          msg: 'Wpisz imię'
        }
      };
    } else if (!isValid || field.value.length < 2) {
      return {
        firstName: {
          error: true,
          msg: 'Wpisz poprawne imię'
        }
      };
    } else { return null; }
  }

  static lastName(field: FormControl): ExtendedValidationErrors {
    const isValid = /^[a-zżźćńółęąś\s\-]+$/i.test(field.value);

    if (!field.value.length) {
      return {
        lastName: {
          error: true,
          msg: 'Wpisz nazwisko'
        }
      };
    } else if (!isValid || field.value.length < 2) {
      return {
        lastName: {
          error: true,
          msg: 'Wpisz poprawne nazwisko'
        }
      };
    } else { return null; }
  }

  static companyName(field: FormControl): ExtendedValidationErrors {
    if (!field.value.length) {
      return {
        companyName: {
          error: true,
          msg: 'Wpisz nazwę firmy'
        }
      };
    } else { return null; }
  }

  static nip(field: FormControl): ExtendedValidationErrors {
    let isValid = true;

    const nip = field.value.replace(/[\ \-\_]/gi, '');

    if (nip.length !== 10) {
      isValid = false;
    } else {
      const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
      let sum = 0;

      for (let i = 0; i < 9; i++) {
        sum += +nip[i] * weights[i];
      }

      const rest = sum %= 11;
      const checksum = rest === 10 ? 0 : rest;

      if (checksum != nip[9]) {
        isValid = false;
      }
    }

    if (!field.value.length) {
      return {
        nip: {
          error: true,
          msg: 'Wpisz NIP'
        }
      };
    } else if (!isValid) {
      return {
        nip: {
          error: true,
          msg: 'Wpisz poprawny NIP'
        }
      };
    } else { return null; }
  }

  static pesel(field: FormControl): ExtendedValidationErrors {
    const PESEL = field.value.replace(/[\ \-\_]/gi, '');
    const isValid = new FormPeselValidator().validate(PESEL);

    if (!field.value.length) {
      return {
        pesel: {
          error: true,
          msg: 'Wpisz PESEL'
        }
      };
    } else if (!isValid) {
      return {
        pesel: {
          error: true,
          msg: 'Wpisz poprawny PESEL'
        }
      };
    } else {
    return null;
 }
}

  static regon(field: FormControl): ExtendedValidationErrors {
    let isValid = false;

    if (field.value.length === 9) {
      const dig = ('' + field.value).split('');
      let control = (
        8 * parseInt(dig[0]) +
        9 * parseInt(dig[1]) +
        2 * parseInt(dig[2]) +
        3 * parseInt(dig[3]) +
        4 * parseInt(dig[4]) +
        5 * parseInt(dig[5]) +
        6 * parseInt(dig[6]) +
        7 * parseInt(dig[7])
      ) % 11;

      if (control === 10) {
        control = 0;
      }

      if (parseInt(dig[8]) === control) {
        isValid = true;
      }
    }

    // Validation for longer REGON [14 digits]
    if (field.value.length === 14) {
      const digLonger = ('' + field.value).split('');
      let controlLonger = (
        2 * parseInt(digLonger[0]) +
        4 * parseInt(digLonger[1]) +
        8 * parseInt(digLonger[2]) +
        5 * parseInt(digLonger[3]) +
        0 * parseInt(digLonger[4]) +
        9 * parseInt(digLonger[5]) +
        7 * parseInt(digLonger[6]) +
        3 * parseInt(digLonger[7]) +
        6 * parseInt(digLonger[8]) +
        1 * parseInt(digLonger[9]) +
        2 * parseInt(digLonger[10]) +
        4 * parseInt(digLonger[11]) +
        8 * parseInt(digLonger[12])
      ) % 11;

      if (controlLonger === 10) {
        controlLonger = 0;
      }

      if (parseInt(digLonger[13]) === controlLonger) {
        isValid = true;
      }
    }

    if (!field.value.length) {
      return {
        regon: {
          error: true,
          msg: 'Wpisz REGON'
        }
      };
    } else if (!isValid) {
      return {
        regon: {
          error: true,
          msg: 'Wpisz poprawny REGON'
        }
      };
    } else { return null; }
  }

  static postalCode(field: FormControl): ExtendedValidationErrors {
    const isValid = /^\d{2,2}-\d{3,3}$/.test(field.value);

    if (!field.value.length) {
      return {
        postalCode: {
          error: true,
          msg: 'Wpisz kod pocztowy'
        }
      };
    } else if (!isValid) {
      return {
        postalCode: {
          error: true,
          msg: 'Wpisz poprawny kod pocztowy'
        }
      };
    } else { return null; }
  }

  static province(field: FormControl): ExtendedValidationErrors {
    if (!field.value.length) {
      return {
        companyName: {
          error: true,
          msg: 'Wybierz województwo'
        }
      };
    } else { return null; }
  }

  static phone(field: FormControl): ExtendedValidationErrors {
    const isValid = /^(\+48\s(\d{3})\s(\d{3})\s(\d{3}))$/.test(field.value);

    if (!field?.value?.length) {
      return {
        phone: {
          error: true,
          msg: 'Wpisz numer telefonu'
        }
      };
    } else if (!isValid) {
      return {
        phone: {
          error: true,
          msg: 'Wpisz poprawny numer telefonu'
        }
      };
    } else { return null; }
  }

  static mnpPhone(field: FormControl): ExtendedValidationErrors {
    const cleaned = field.value?.replace(/[\s\u00A0-]/g, '');
    const isValid = /^\+48\d{9}$/.test(cleaned);

    if (!cleaned?.length) {
      return {
        number: {
          error : true,
          msg : 'Wpisz numer telefonu'
        }
      };
    } else if (!isValid) {
      return {
        number: {
          error : true,
          msg : 'Podany numer telefonu jest nieprawidłowy'
        }
      };
    }
  }

  static email(field: FormControl): ExtendedValidationErrors {
    const polishLetters = /[ąĄćĆęĘłŁńŃóÓśŚźŹżŻ]/;
    const validator = new EmailValidator();

    if (!field.value.length) {
      return {
        email: {
          error: true,
          msg: 'Wpisz e-mail'
        }
      };
    } else if (field.value.includes(' ')) {
      return {
        email: {
          error: true,
          msg: 'Adres e-mail nie może zawierać spacji'
        }
      };
    } else if ((field.value.split('@').length - 1) > 1) {
      return {
        email: {
          error: true,
          msg: 'Adres e-mail powinien zawierać tylko jedną @'
        }
      };
    } else if (field.value.match(polishLetters)) {
      return {
        email: {
          error: true,
          msg: 'Adres e-mail nie może zawierać polskich znaków'
        }
      };
    } else if (!(validator.isValid(field.value) && !field.value.match(polishLetters))) {
      return {
        email: {
          error: true,
          msg: 'Wpisz poprawny adres e-mail'
        }
      };
    } else { return null; }
  }

  static businessStartDate(field: FormControl): ExtendedValidationErrors {
    const today = Date.now();
    const exp = field.value.split('.').reverse().join('-');
    const expiration = new Date(exp).getTime();
    const invalidDate = !moment(exp).isValid();

    if (expiration >= today || !field.value.length) {
      return {
        businessStartDate: {
          error: true,
          msg: 'Data rozpoczęcia działalności gospodarczej nie może być późniejsza niż dzień złożenia zamówienia'
        }
      };
    } else if (invalidDate) {
      return {
        businessStartDate: {
          error: true,
          msg: 'Wpisz poprawną datę'
        }
      };
    } else {
      return null;
    }
  }

  static validateExpiryDate(field: FormControl): ExtendedValidationErrors {
    const fieldName = field['formNativeElement']?.getAttribute('id');
    const errorMsg = fieldName === 'id-expiry-date' ? 'dowodu osobistego' : 'mDowodu';

    const expiration = moment(field.value.split('.').reverse().join('-'));
    const invalidDate = moment().diff(expiration, 'years') <= -10 || !expiration || !expiration.isValid();
    const endDate = moment().diff(expiration, 'days') > -7 && expiration.isValid();
    const IndefiniteId = field?.value === '31.12.2999';

    if (!field.value.length) {
      return {
        [fieldName]: {
          error: true,
          msg: `Wpisz datę ważności ${errorMsg}`
        }
      };
    } else if (endDate) {
      return {
        [fieldName]: {
          error: true,
          msg: `Data ważności ${errorMsg} musi być dłuższa niż 7 dni`
        }
      };
    } else if (invalidDate && !IndefiniteId) {
      return {
        [fieldName]: {
          error: true,
          msg: `Wpisz poprawną datę ważności ${errorMsg}`
        }
      };
    } else {
      return null;
    }
  }

  static city(field: FormControl): ExtendedValidationErrors {
    const isValid = /^(?=.*[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ])[\w\dąćęłńóśźżĄĆĘŁŃÓŚŹŻ.,;:/\\'_\"()\s\-]+$/i.test(field.value);

    if (!field.value.length) {
      return {
        city: {
          error: true,
          msg: 'Wpisz miejscowość'
        }
      };
    } else if (!isValid || field.value.length < 1) {
      return {
        city: {
          error: true,
          msg: 'Wpisz poprawną miejscowość'
        }
      };
    } else { return null; }
  }

  static street(field: FormControl): ExtendedValidationErrors {
    const isValid = /^(?=.*[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ])[\w\dąćęłńóśźżĄĆĘŁŃÓŚŹŻ.,;:/\\'_\"()\s\-]+$/i.test(field.value);

    if (!field.value.length) {
      return {
        street: {
          error: true,
          msg: 'Wpisz ulicę'
        }
      };
    } else if (!isValid || field.value.length < 1) {
      return {
        street: {
          error: true,
          msg: 'Wpisz poprawną ulicę'
        }
      };
    } else { return null; }
  }

  static streetNumber(field: FormControl): ExtendedValidationErrors {
    const isValid = /^[a-zżźćńółęąś\s0-9\-.,;:/'_\""()]+$/i.test(field.value);

    if (!field.value.length) {
      return {
        streetNumber: {
          error: true,
          msg: 'Wpisz numer domu'
        }
      };
    } else if (!isValid) {
      return {
        streetNumber: {
          error: true,
          msg: 'Wpisz poprawny numer domu'
        }
      };
    } else { return null; }
  }

  static apartmentNumber(field: FormControl): ExtendedValidationErrors {
    const isValid = /^[a-zżźćńółęąś\s0-9\-.,;:/'_\""()]+$/i.test(field.value);

    if (!isValid && field.value.length > 0) {
      return {
        apartmentNumber: {
          error: true,
          msg: 'Wpisz poprawny numer lokalu'
        }
      };
    } else { return null; }
  }

  static password(field: FormControl): ExtendedValidationErrors {
    const correctValue = field.value.match(/^(?=.*[a-zżźćńółęąśA-ZŻŹĆŃÓŁĘĄŚ\d].*)[a-zżźćńółęąśA-ZŻŹĆŃÓŁĘĄŚ\d]{6,9}$/g);

    if (!field.value.length) {
      return {
        password: {
          error: true,
          msg: 'Utwórz hasło'
        }
      };
    } else if (field.value.length < 6) {
      return {
        password: {
          error: true,
          msg: 'Podane hasło jest za krótkie'
        }
      };
    } else if (!correctValue) {
      return {
        password: {
          error: true,
          msg: 'Podane hasło jest nieprawidłowe'
        }
      };
    } else { return null; }
  }

  static personalIdNumber(field: FormControl): ExtendedValidationErrors {
    const rule = {
      personalIdNumber: {
        error: true,
        msg: 'Wpisz poprawną serię i numer dowodu'
      }
    };

    const letterValues = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
      'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T',
      'U', 'V', 'W', 'X', 'Y', 'Z'
    ];

    if (!field.value.length) {
      rule.personalIdNumber.msg = 'Wpisz serię i numer dowodu';
      return rule;
    }

    for (let i = 0; i < 3; i++) {
      const charIndex = letterValues.indexOf(field.value[i]);

      if (charIndex < 10) {
        return rule;
      }
    }

    for (let i = 3; i < 9; i++) {
      const charIndex = letterValues.indexOf(field.value[i]);

      if (charIndex < 0 || charIndex > 9) {
        return rule;
      }
    }

    const weights = [7, 3, 1, 7, 3, 1, 7, 3];
    const checksum = letterValues.indexOf(field.value[3]);
    let sum = 0;

    for (let i = 0; i < 8; i++) {
      let index = i;

      if (index >= 3) { index += 1; }

      const charIndex = letterValues.indexOf(field.value[index]);

      sum += charIndex * weights[i];
    }

    if (sum % 10 !== checksum) {
      return rule;
    }

    return null;
  }

  static mPersonalIdNumber(field): ExtendedValidationErrors {
    const rule = {
      mPersonalIdNumber: {
        error: true,
        msg: 'Wpisz poprawną serię i numer mDowodu'
      }
    };

    const letterValues = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
      'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T',
      'U', 'V', 'W', 'X', 'Y', 'Z'
    ];

    if (!field.value) {
      rule.mPersonalIdNumber.msg = 'Wpisz serię i numer mDowodu';
      return rule;
    }

    for (let i = 0; i < 4; i++) {
      const charIndex = letterValues.indexOf(field.value[i]);
      if (charIndex < 10 || field.value[0] !== letterValues[22]) {
        return rule;
      }
    }

    for (let i = 4; i < 9; i++) {
      const charIndex = letterValues.indexOf(field.value[i]);

      if (charIndex < 0 || charIndex > 9) {
        return rule;
      }
    }

    const weights = [7, 3, 1, 7, 3, 1, 7, 3];
    const checksum = letterValues.indexOf(field.value[4]);
    let sum = 0;

    for (let i = 0; i < 8; i++) {
      let index = i;

      if (index >= 4) index += 1;

      const charIndex = letterValues.indexOf(field.value[index]);

      sum += charIndex * weights[i];
    }

    if (sum % 10 !== checksum) {
      return rule;
    }

    return null;
  }
}
